/* ============================================================
   MOSAIK — GEOMETRY
   Pure functions: given the design options + viewport, compute the
   pixel layout and the tile / border / corner cell rectangles.
   No canvas, no DOM state — so any layer (tiles, borders, corners,
   lights, ...) can reason about positions the same way.
   ============================================================ */
window.MosaikGeometry = {

  /* ---------- LES RÈGLES GRADUÉES ----------
     Le plan ne porte plus « Largeur : 8 m » / « Longueur : 4 m » mais deux règles
     (bande du haut = largeur, bande de droite = longueur). Elles ne sont PAS
     dessinées dans le canvas : elles vivent dans deux bandes fixées aux bords du
     PANNEAU (mosaik-render.js, syncRulers), et se recalculent à chaque zoom et à
     chaque déplacement. Dessinées avec le plan, elles s'en iraient avec lui — un
     visiteur zoomé au milieu de son sol n'aurait plus une mesure à l'écran, qui
     est justement le moment où il en a le plus besoin.

     Les bandes sont réservées en CSS (padding du panneau) : le plan est ajusté
     dans ce qu'il en reste, il ne passe donc jamais dessous au repos. */
  RULER:         { h:26, w:26 },   // bureau : bande haute, bande droite (px CSS)
  RULER_COMPACT: { h:19, w:22 },   // mobile : mêmes crans, tout est plus serré

  /* Returns everything needed to place cells on the canvas.
     availW / availH = the drawing box the plan must fit into, in CSS pixels.
     The caller decides what that box is: the left pane on the desktop split
     view, the plan's share of a phone screen on mobile (see mosaik-render.js).

     minTile = la dalle en dessous de laquelle on refuse de descendre, même s'il
     faut pour cela sortir de la boîte : une dalle trop petite n'est plus visable.
     Le plan déborde alors de son panneau, qui le rogne et se laisse déplacer.
     50 px au doigt comme à la souris depuis toujours ; le mobile passe désormais
     MOBILE_MIN_TILE (plus bas), parce que son panneau est court et que 50 px y
     faisaient déborder jusqu'aux petits sols, qui tenaient très bien à 40. */
  computeLayout:function(options, availW, availH, minTile){
    const cols = Math.ceil(options.width / options.mosaikWidth),
          rows = Math.ceil(options.height / options.mosaikHeight),
          // The border strips add 2/3 of a tile to each axis (bW = tileW/3 on
          // both sides). Reserve that here, otherwise the finished canvas
          // overflows its box by two thirds of a tile — which, in the desktop
          // split view, means a scrollbar on a plan that should have fitted.
          AW = Math.floor(availW * cols / (cols + 2/3)),
          AH = Math.floor(availH * rows / (rows + 2/3)),
          RF = options.width / options.height,
          RA = AW / AH;
    let drawW, drawH;
    if(RF > RA){
        drawW = AW;
        drawH = drawW / RF;
    }else{
        drawH = AH;
        drawW = drawH * RF;
    }
    const MIN = minTile > 0 ? minTile : 50;
    let tileW = Math.floor(drawW / cols), tileH = Math.floor(drawH / rows);
    if(tileW < MIN){
        const r = tileW / tileH,
              mW = Math.floor(AW / cols);
        tileW = (mW < MIN ? mW : MIN);
        tileH = Math.floor(tileW / r);
    }
    const bW = Math.floor(tileW / 3), bH = Math.floor(tileH / 3),
          // le canvas EST le plan (dalles + bandes de bordure) : ox/oy/planW/planH
          // le disent explicitement, pour que les cellules de bordure et le cadre
          // se placent par rapport au plan et non par rapport au canvas.
          planW = cols * tileW + 2 * bW, planH = rows * tileH + 2 * bH,
          ox = 0, oy = 0,
          cW = planW, cH = planH,
          xOff = ox + bW, yOff = oy + bH;
    return { cols, rows, tileW, tileH, bW, bH, cW, cH, xOff, yOff,
             ox, oy, planW, planH };
  },

  /* Origine du plan dans le canvas. Les stubs de vignettes (mosaik-templates.js)
     n'ont ni ox ni oy : ils n'ont jamais eu de gouttière, d'où le repli sur 0. */
  originOf:function(L){ return { ox:L.ox || 0, oy:L.oy || 0 }; },

  /* Border strips down the 4 edges. Each gets a stable idx + side. */
  buildBorderCells:function(L){
    const cells = [];
    const { ox, oy } = this.originOf(L);
    let bI = 0;
    for(let i = 0; i < L.rows; i++){
        const y = L.yOff + i * L.tileH;
        cells.push({ x:ox,                     y:y, w:L.bW,     h:L.tileH, idx:bI++, side:'left'  });
        cells.push({ x:L.xOff + L.cols*L.tileW, y:y, w:L.bW,     h:L.tileH, idx:bI++, side:'right' });
    }
    for(let j = 0; j < L.cols; j++){
        const x = L.xOff + j * L.tileW;
        cells.push({ x:x, y:oy,                      w:L.tileW, h:L.bH, idx:bI++, side:'top'    });
        cells.push({ x:x, y:L.yOff + L.rows*L.tileH, w:L.tileW, h:L.bH, idx:bI++, side:'bottom' });
    }
    return cells;
  },

  /* The 4 corner squares, idx 0..3 (TL, TR, BL, BR). */
  buildCornerCells:function(L){
    const { ox, oy } = this.originOf(L);
    const pW = L.planW || L.cW, pH = L.planH || L.cH;
    return [
      { x:ox,             y:oy,             w:L.bW, h:L.bH, idx:0 },
      { x:ox+pW-L.bW,     y:oy,             w:L.bW, h:L.bH, idx:1 },
      { x:ox,             y:oy+pH-L.bH,     w:L.bW, h:L.bH, idx:2 },
      { x:ox+pW-L.bW,     y:oy+pH-L.bH,     w:L.bW, h:L.bH, idx:3 }
    ];
  },

  /* ---------- CRANS D'UNE RÈGLE ----------
     `meters` = la longueur RÉELLE de la portée mesurée, `px` = ce qu'elle occupe
     à l'écran. La portée mesurée est la SURFACE DALLÉE : cols × la largeur de
     dalle de la collection (mosaik-data.js), et non la dimension saisie par le
     visiteur — un sol de 8 m posé en dalles de 39,5 cm en fait 8,295, et c'est
     ce sol-là qu'il dessine. Les bandes de bordure restent hors mesure : leur
     largeur réelle n'est pas dans les données.

     Un cran tous les 50 cm, un repère CHIFFRÉ tous les `step` mètres — le pas
     monte (1, 2, 5, 10, 20, 50 m) tant que deux étiquettes voisines ne tiennent
     pas côte à côte, sinon un grand sol donnerait une bouillie de chiffres.
     Le dernier cran porte toujours la mesure exacte de la portée.

     Retourne [{ pos, m, kind }] avec kind = 'minor' | 'major' | 'end' ; seuls
     'major' et 'end' portent une étiquette. */
  MINOR_M: 0.5,          // les traits de 50 cm demandés
  MINOR_MIN_PX: 7,       // sous quoi ils se touchent : on ne les dessine plus
  rulerTicks:function(meters, px, labelPx){
    const out = [];
    if(!(meters > 0) || !(px > 0)) return out;
    const ppm  = px / meters,
          need = labelPx || 34;
    const step = [1, 2, 5, 10, 20, 50].find(s => s * ppm >= need) || 50;
    const showMinor = this.MINOR_M * ppm >= this.MINOR_MIN_PX;
    // tout en CENTIMÈTRES entiers : 0.1+0.2 en mètres ne retombe jamais juste,
    // et un cran « presque 3 m » serait chiffré 2,9999999
    const totalCm = Math.round(meters * 100),
          minorCm = Math.round(this.MINOR_M * 100),
          stepCm  = step * 100;
    for(let c = 0; c <= totalCm; c += minorCm){
      const major = (c % stepCm === 0);
      if(!major && !showMinor) continue;
      out.push({ pos:(c / 100) * ppm, m:c / 100, kind:major ? 'major' : 'minor' });
    }
    // la mesure exacte du bout : elle tombe rarement sur un cran rond
    const last = out[out.length - 1];
    if(!last || Math.round(last.m * 100) !== totalCm)
      out.push({ pos:px, m:totalCm / 100, kind:'end' });
    else last.kind = 'end';
    // le bout et le dernier chiffre rond se marcheraient dessus : le bout gagne,
    // c'est lui qui donne la dimension du sol
    const end = out[out.length - 1];
    for(let i = out.length - 2; i >= 0; i--){
      if(out[i].kind !== 'major') continue;
      if(end.pos - out[i].pos < need) out[i].kind = 'minor';
      break;
    }
    return out;
  }
};
